import { types as CardType } from 'credit-card-type';

import { SESSIONSTORAGE_KEY_REWARDS_INFO } from 'constants/rewardsInfo';
import {
  CANCELLED,
  COMPLETED,
  DECLINED_CANCELLED,
  DELIVERED,
  RETURNED,
  RETURNING,
  RETURNING_ALT,
  SHIPPED
} from 'constants/orderStatuses';
import { getFromSessionStorageWithSessionId, saveToSessionStorageWithSessionId } from 'helpers/ClientUtils';

const returningStates = [
  RETURNING,
  RETURNED,
  RETURNING_ALT
];

const cancelStates = [
  CANCELLED,
  DECLINED_CANCELLED
];

export function getPaymentType(creditCardNumber) {
  switch (parseInt(creditCardNumber.slice(0, 1))) {
    case 3:
      return 'AmericanExpress';
    case 4:
      return 'Visa';
    case 5:
      return 'MasterCard';
    case 6:
      return 'Discover';
    default:
      return null;
  }
}

export function isCreditCardExpired(creditCard) {
  const currDate = new Date(Date.now());
  const expMonth = parseInt(creditCard.expirationMonth);
  const expYear = parseInt(creditCard.expirationYear);
  return expYear < currDate.getFullYear() || (expYear === currDate.getFullYear() && expMonth < currDate.getMonth() + 1);
}

export function formatCreditCardExpiration(expiration) {
  let [ month, year ] = expiration.split(/\W|\//);

  if (month.length === 1) {
    month = `0${month}`;
  }

  if (year.length === 2) {
    year = `20${year}`;
  }

  return {
    expirationMonth: month,
    expirationYear: year
  };
}

export function isExpirationDateValid(expMonth, expYear) {
  const today = new Date();
  const selectedDate = new Date();
  selectedDate.setFullYear(expYear, expMonth, 1);
  return selectedDate >= today;
}

export function formatCreditCard(number) {
  return number.replace(/\D/g, '');
}

export function hasTracking(order) {
  const { lineItems } = order;
  return lineItems.every(item => item.shipment.tracking && item.shipment.tracking.events && item.shipment.tracking.events.length);
}

export function hasOrderBeenShipped(lineItems) {
  return lineItems.every(({ state }) => state === SHIPPED || state === RETURNING || state === RETURNED || state === COMPLETED);
}

export function hasOrderBeenDelivered(lineItems) {
  return hasOrderBeenShipped(lineItems) && lineItems.every(({ state, shipment }) => {
    const { tracking } = shipment;
    return tracking.statusCode === DELIVERED || state === COMPLETED;
  });
}

export function isOrderCancellable(lineItems) {
  return lineItems.some(lineItem => lineItem.cancellable);
}

export function isOrderExchangeable(lineItems) {
  return lineItems.some(lineItem => lineItem.exchangeable);
}

export function isOrderReturnable(lineItems, makeNonReturnable = false) {
  return lineItems.some(lineItem => (makeNonReturnable ? false : lineItem.returnable));
}

export function isOrderContainingShipped(lineItems) {
  return lineItems.some(({ state }) => state === SHIPPED);
}

export function reverseReturnUrlLookup(url) {
  if (/^\/addresses$/.test(url)) {
    return 'Shipping Information';
  } else if (/^\/addresses\/new$/.test(url)) {
    return 'Add New Address';
  } else if (/^\/payments$/.test(url)) {
    return 'Manage Payments';
  } else if (/^\/payments\/new$/.test(url)) {
    return 'Add New Payment';
  } else if (/^\/payments\/[a-zA-Z0-9-_]+\/edit$/.test(url)) {
    return 'Edit Payment Method';
  } else if (/^\/orders\/[a-zA-Z0-9-_]+$/.test(url)) {
    return 'Order Information';
  }
}

export function normalizePaymentType(paymentType) {
  switch (paymentType) {
    case 'Visa':
      return CardType.VISA;
    case 'MasterCard':
      return CardType.MASTERCARD;
    case 'Discover':
      return CardType.DISCOVER;
    case 'AmericanExpress':
      return CardType.AMERICAN_EXPRESS;
  }
}

export function generateProductUrl(product, isLegacy) {
  const { asin, productUrl } = product;
  if (isLegacy) {
    return productUrl;
  } else {
    return `/p/asin/${asin}`;
  }
}

export function isItemInReturnState(itemState) {
  return returningStates.indexOf(itemState) > -1;
}

export function isItemInCancelState(itemState) {
  return cancelStates.indexOf(itemState) > -1;
}

/* query-string library not correctly parsing '+' symbol and sending spaces for subscriptions endpoint */
export function replaceAuthTokenSpace(authToken) {
  if (authToken) {
    return authToken.split(' ').join('+');
  }
}

/*
  Generates unique ids for each lineItem and assigns them.

  The ids have the following format:
  - {order id}-{returnable-fingerprint}-{item code}-{index}

  Example:
  - 113-5838898-9370642-r-21136263204721-0

  {returnable-fingerprint} specifies whether the item is (r)eturnable.
  In the example above, '-r-' means the item is indeed returnable.

  {index} differentiates between two or more line items with
  identical order ids, attributes and codes.
*/
export function injectUniqueIdsToLineItems({ orderId, lineItems = [] }) {
  const idSuffixes = {};
  return lineItems.map(lineItem => {
    const { id: lineItemId, returnable } = lineItem;
    const returnableFingerPrint = returnable ? '-r' : '';
    const idPrefix = `${orderId}${returnableFingerPrint}-${lineItemId}`;
    idSuffixes[idPrefix] = idSuffixes[idPrefix] || 0;
    const idSuffix = idSuffixes[idPrefix]++;
    return {
      ...lineItem,
      itemUniqueId: `${idPrefix}-${idSuffix}`
    };
  });
}

/* Combine info from fetchShipmentTracking call with order data in state */
export function injectTrackingToLineItems({ lineItems }, trackingInfo) {
  const updatedLineItems = [];
  lineItems.forEach(lineItem => {
    const lineItemCopy = Object.assign({}, lineItem);
    const { shipment: { id } } = lineItem;
    trackingInfo.forEach(info => {
      const { id: trackingInfoShipmentId } = info;
      if (id === trackingInfoShipmentId) {
        lineItemCopy.shipment = info;
      }
    });
    updatedLineItems.push(lineItemCopy);
  });
  return updatedLineItems;
}

export function getAsinFromProductUrl(productUrl) {
  if (productUrl && productUrl.includes('asin')) {
    return productUrl.split(/\/asin\//)[1];
  }
  return null;
}

export function storeRewardsInfo(data = {}, sessionId, saveToSessionStorageWithSessionIdFn = saveToSessionStorageWithSessionId) {
  return saveToSessionStorageWithSessionIdFn(SESSIONSTORAGE_KEY_REWARDS_INFO, data, sessionId);
}

// This is OWASP-obvious, but just in case:
// NEVER use this function to retrieve rewards info to be processed by the server later.  Ever.
// Its potentially tainted client-side result must only be used for displaying/informational purposes.
export function getCachedRewardsInfoAndValidate(sessionId, getFromSessionStorageWithSessionIdFn = getFromSessionStorageWithSessionId) {
  return getFromSessionStorageWithSessionIdFn(SESSIONSTORAGE_KEY_REWARDS_INFO, sessionId);
}
